import * as React from "react";
import { ViewPinFormStep1 } from "./Steps/Step1";
import { ErrorStep } from "./Steps/ErrorStep";
import { SuccessStep } from "./Steps/SuccessStep";
import { GeneralModal } from "../Common/GeneralModal/GeneralModal";
import { drupalSettings } from "../../utils/config";
import { useLoader } from "../Common/GeneralLoader/GeneralLoaderProvider";
import { sendSMSApi, viewCardPinApi } from "../../api/api";
import { ScaVerify } from "../ScaAirwallex/ScaVerify";

export function ViewPin() {
  const [step, setStep] = React.useState(1);
  const [open, setOpen] = React.useState(true);
  const { showLoader, hideLoader } = useLoader();
  const [iframeSrc, setIframeSrc] = React.useState("");
  const [scaSessionCode, setScaSessionCode] = React.useState(null);
  const initialFetchDone = React.useRef(false);

  React.useEffect(() => {
    showLoader();
    if (drupalSettings?.user?.sca_access) {
      return;
    }
    sendSMSApi({
      uid: drupalSettings?.user?.uid,
    })
      .then(() => hideLoader())
      .catch(() => hideLoader());
  }, []);

  // Step 1 (sca_access only): call view-pin without token first; on 403 get session code and show SCA with it.
  React.useEffect(() => {
    if (!drupalSettings?.user?.sca_access || step !== 1 || initialFetchDone.current) return;
    const card_id = drupalSettings?.card_id;
    const connected_account_id = drupalSettings?.connected_account_id;
    if (!card_id || !connected_account_id) {
      hideLoader();
      return;
    }
    initialFetchDone.current = true;
    showLoader();
    viewCardPinApi({ card_id, connected_account_id })
      .then((data) => {
        hideLoader();
        if (data?.iframe_src) {
          setIframeSrc(data.iframe_src);
          setStep(0);
        } else {
          setStep(-1);
        }
      })
      .catch((err) => {
        hideLoader();
        if (err?.response?.status === 403) {
          const code = err?.response?.headers?.["x-sca-session-code"];
          if (code) {
            setScaSessionCode(code);
            setStep(2);
            return;
          }
        }
        setStep(-1);
      });
  }, [step]);

  const getStepComponent = (step) => {
    switch (step) {
      case -1:
        return <ErrorStep />;
      case 0:
        return <SuccessStep setStep={setStep} iframeSrc={iframeSrc} />;
      case 1:
        return !drupalSettings?.user?.sca_access ? (
          <ViewPinFormStep1 setStep={setStep} />
        ) : (
          <p style={{ textAlign: "center" }}>{Drupal.t("Loading your PIN…")}</p>
        );
      case 2:
        return (
          <ScaVerify
            key="sca-pantokens-pin"
            scaSessionCode={scaSessionCode}
            setStep={setStep}
            onCancel={() => setOpen(false)}
            onVerificationSucceed={async (payload) => {
              const token = payload?.token;
              if (!token) {
                setStep(-1);
                return;
              }
              showLoader();
              try {
                const data = await viewCardPinApi({
                  card_id: drupalSettings?.card_id,
                  connected_account_id: drupalSettings?.connected_account_id,
                  sca_token: token,
                });
                hideLoader();
                if (data?.iframe_src) {
                  setIframeSrc(data.iframe_src);
                  setStep(0);
                } else {
                  setStep(-1);
                }
              } catch {
                hideLoader();
                setStep(-1);
              }
            }}
            onVerificationFailed={() => setStep(-1)}
          />
        );
    }
  };

  return (
    <GeneralModal
      open={open}
      setOpen={setOpen}
      className={
        step == 1
          ? "view-pin-verify-form-wrapper"
          : step == -1
          ? "view-pin-verify-form-wrapper error-modal"
          : "view-pin-verify-form-wrapper success-modal"
      }
    >
      {getStepComponent(step)}
    </GeneralModal>
  );
}
